/**
 * Onboarding Service — Data access layer for the onboarding system.
 *
 * Handles both formats transparently:
 *   - OLD: flat columns on `onboarding_checklist` table
 *   - NEW: normalized rows in `onboarding_progress` table
 *
 * Read strategy: try `onboarding_progress` first; fall back to `onboarding_checklist`
 * Write strategy: always write to `onboarding_progress` (never to old table)
 *
 * This service is stateless — call functions with required parameters.
 * For React state management, use the `useOnboardingSteps` hook.
 */

import { supabase } from '../lib/supabase';
import { safeQuery } from '../utils/safeSupabase';
import { logAudit, AUDIT_ACTIONS } from './auditService';
import { OLD_STEPS, OLD_TO_NEW_STEP_MAP, getApplicableSteps, isOnboardingStepApplicable } from '../utils/onboardingSteps';

async function fetchJobForOnboarding(jobId) {
  if (!jobId) return null;

  const { data, error } = await supabase
    .from('job')
    .select('id, uses_pipelines')
    .eq('id', jobId)
    .single();

  if (error) {
    console.error('Failed to fetch job for onboarding applicability:', error);
    return null;
  }

  return data;
}

function normalizeTemplateSteps(templateSteps = [], job = null) {
  return (templateSteps || [])
    .filter(Boolean)
    .filter(step => step.isEnabled !== false && step.is_enabled !== false)
    .filter(step => isOnboardingStepApplicable(step, job))
    .map((step, index) => ({
      id: step.id || step.step_id,
      key: step.key || step.id || step.step_id,
      label: step.label || step.step?.label || step.step_id || step.id || `Step ${index + 1}`,
      description: step.description || step.step?.description || '',
      icon: step.icon || step.step?.icon || '📋',
      category: step.category || step.step?.category || 'admin_managed',
      isRequired: step.isRequired ?? step.is_required ?? true,
      isEnabled: step.isEnabled ?? step.is_enabled ?? true,
      sortOrder: step.sortOrder ?? step.sort_order ?? ((index + 1) * 10),
      config: step.config || {},
    }))
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

async function resolveTemplateStepsForAssignment({ orgId, jobId, templateSteps = [] }) {
  let job = null;

  if (jobId) {
    job = await fetchJobForOnboarding(jobId);
  }

  const normalizedProvided = normalizeTemplateSteps(templateSteps, job);
  if (normalizedProvided.length > 0) {
    return normalizedProvided;
  }

  if (!orgId) return [];

  const { data, error } = await supabase
    .from('onboarding_template')
    .select('step_id, sort_order, is_required, is_enabled, config, step:step_id(*)')
    .eq('org_id', orgId)
    .eq('is_enabled', true)
    .order('sort_order');

  if (error) {
    console.error('Failed to resolve onboarding template steps:', error);
    return [];
  }

  return normalizeTemplateSteps(data || [], job);
}

// ─── Read Operations ───────────────────────────────────────

/**
 * Fetch onboarding progress for an assignment.
 * Tries new `onboarding_progress` table first, falls back to old `onboarding_checklist`.
 *
 * @param {string} assignmentId - UUID of the assignment
 * @returns {Object} { rows: [{step_id, status, completed_at, completed_by}], source: 'progress'|'checklist' }
 */
export async function fetchProgress(assignmentId) {
  if (!assignmentId) return { rows: [], source: 'none' };

  // Try new table first
  const { data: progressRows, error: progressError } = await supabase
    .from('onboarding_progress')
    .select('*')
    .eq('assignment_id', assignmentId)
    .order('created_at');

  if (!progressError && progressRows && progressRows.length > 0) {
    return { rows: progressRows, source: 'progress' };
  }

  // Fall back to old table
  const { data: checklistRow, error: checklistError } = await supabase
    .from('onboarding_checklist')
    .select('*')
    .eq('assignment_id', assignmentId)
    .single();

  if (!checklistError && checklistRow) {
    // Transform flat columns into normalized rows
    const rows = OLD_STEPS
      .map(step => {
        const status = checklistRow[step.key];
        if (!status || status === 'N/A') return null;
        return {
          id: null, // no UUID — this is a virtual row
          assignment_id: assignmentId,
          individual_id: checklistRow.individual_id,
          job_id: checklistRow.job_id,
          org_id: checklistRow.org_id,
          step_id: OLD_TO_NEW_STEP_MAP[step.key] || step.key,
          status: status || 'Not Started',
          completed_at: status === 'Completed' ? checklistRow.updated_at : null,
          completed_by: status === 'Completed' ? 'admin' : null,
          metadata: {},
          // Keep the old key for backward compat in UI
          _legacy_key: step.key,
        };
      })
      .filter(Boolean);

    return { rows, source: 'checklist' };
  }

  return { rows: [], source: 'none' };
}

/**
 * Fetch onboarding progress for multiple assignments at once.
 * Returns a map: { assignmentId: { rows, source } }
 */
export async function fetchProgressBatch(assignmentIds) {
  if (!assignmentIds?.length) return {};

  // Try new table first
  const { data: allProgress } = await safeQuery(
    supabase
      .from('onboarding_progress')
      .select('*')
      .in('assignment_id', assignmentIds)
      .order('created_at'),
    'onboarding-fetchProgressBatch'
  );

  // Try old table
  const { data: allChecklists } = await safeQuery(
    supabase
      .from('onboarding_checklist')
      .select('*')
      .in('assignment_id', assignmentIds),
    'onboarding-fetchChecklistBatch'
  );

  const result = {};

  for (const assignmentId of assignmentIds) {
    const progressRows = (allProgress || []).filter(p => p.assignment_id === assignmentId);

    if (progressRows.length > 0) {
      result[assignmentId] = { rows: progressRows, source: 'progress' };
    } else {
      // Fall back to old table
      const checklistRow = (allChecklists || []).find(c => c.assignment_id === assignmentId);
      if (checklistRow) {
        const rows = OLD_STEPS
          .map(step => {
            const status = checklistRow[step.key];
            if (!status || status === 'N/A') return null;
            return {
              id: null,
              assignment_id: assignmentId,
              individual_id: checklistRow.individual_id,
              job_id: checklistRow.job_id,
              org_id: checklistRow.org_id,
              step_id: OLD_TO_NEW_STEP_MAP[step.key] || step.key,
              status: status || 'Not Started',
              completed_at: status === 'Completed' ? checklistRow.updated_at : null,
              completed_by: status === 'Completed' ? 'admin' : null,
              metadata: {},
              _legacy_key: step.key,
            };
          })
          .filter(Boolean);
        result[assignmentId] = { rows, source: 'checklist' };
      } else {
        result[assignmentId] = { rows: [], source: 'none' };
      }
    }
  }

  return result;
}

// ─── Write Operations (always to new table) ────────────────

/**
 * Update a single step's status.
 *
 * @param {string} assignmentId
 * @param {string} stepId - step_library ID (e.g., 'general_onboarding', 'tool_setup_deel')
 * @param {string} newStatus - 'Not Started', 'In Progress', 'Completed', 'Skipped'
 * @param {string} completedBy - who completed it: 'self', 'admin', 'system', or email
 */
export async function updateStepStatus(assignmentId, stepId, newStatus, completedBy = 'admin') {
  const updates = {
    status: newStatus,
    updated_at: new Date().toISOString(),
    completed_at: newStatus === 'Completed' ? new Date().toISOString() : null,
    completed_by: newStatus === 'Completed' ? completedBy : null,
  };

  // Try to update existing row
  const { data, error } = await supabase
    .from('onboarding_progress')
    .update(updates)
    .eq('assignment_id', assignmentId)
    .eq('step_id', stepId)
    .select()
    .single();

  if (error || !data) {
    // Row doesn't exist in new table — this assignment might only have old-format data.
    // We can't update the old table (it uses column names, not step_ids).
    // Instead, create a new progress row.
    console.warn(`No progress row for ${assignmentId}/${stepId} — creating one`);
    // We need individual_id, job_id, org_id from the assignment
    const { data: assignment } = await supabase
      .from('assignment')
      .select('individual_id, job_id, org_id')
      .eq('id', assignmentId)
      .single();

    if (assignment) {
      const { error: insertError } = await supabase
        .from('onboarding_progress')
        .insert({
          assignment_id: assignmentId,
          individual_id: assignment.individual_id,
          job_id: assignment.job_id,
          org_id: assignment.org_id,
          step_id: stepId,
          ...updates,
        });
      if (insertError) {
        console.error('Failed to create progress row:', insertError);
        return { success: false, error: insertError };
      }
    } else {
      return { success: false, error: 'No assignment found' };
    }
  }

  return { success: true, status: newStatus };
}

/**
 * Create progress rows for a new assignment based on the org's template.
 *
 * @param {Object} params
 * @param {string} params.assignmentId
 * @param {string} params.individualId
 * @param {string} params.jobId
 * @param {string} params.orgId
 * @param {Array} params.templateSteps - from useOnboardingSteps().steps
 * @param {string} [params.defaultStatus='Not Started'] - initial status for all steps
 */
export async function createProgressForAssignment({
  assignmentId, individualId, jobId, orgId, templateSteps, defaultStatus = 'Not Started',
}) {
  const resolvedTemplateSteps = await resolveTemplateStepsForAssignment({ orgId, jobId, templateSteps });

  if (!resolvedTemplateSteps?.length) {
    console.warn('No template steps available for createProgressForAssignment');
    return { success: false, error: 'No template steps' };
  }

  const rows = resolvedTemplateSteps.map(step => ({
    assignment_id: assignmentId,
    individual_id: individualId,
    job_id: jobId,
    org_id: orgId,
    step_id: step.id,
    status: defaultStatus,
    completed_at: defaultStatus === 'Completed' ? new Date().toISOString() : null,
    completed_by: defaultStatus === 'Completed' ? 'system' : null,
  }));

  const { error } = await supabase.from('onboarding_progress').insert(rows);

  if (error) {
    console.error('Failed to create progress rows:', error);
    return { success: false, error };
  }

  return { success: true, count: rows.length };
}

/**
 * Mark all steps as completed for an assignment (bypass/fast-track).
 */
export async function bypassAllSteps(assignmentId, completedBy = 'admin') {
  const { error } = await supabase
    .from('onboarding_progress')
    .update({
      status: 'Completed',
      completed_at: new Date().toISOString(),
      completed_by: completedBy,
      updated_at: new Date().toISOString(),
    })
    .eq('assignment_id', assignmentId);

  if (error) {
    console.error('Failed to bypass onboarding:', error);
    return { success: false, error };
  }

  // Phase 11 slot 5: admin fast-track of an annotator's onboarding.
  logAudit(AUDIT_ACTIONS.BYPASS_ONBOARDING, {
    entityType: 'assignment',
    entityId: assignmentId,
    details: { completedBy },
  });

  return { success: true };
}

export function getOnboardingStepStatus(progressData, step) {
  if (!progressData || !step) return 'Not Started';

  const rows = Array.isArray(progressData) ? progressData : progressData._rows || [];
  if (rows.length > 0) {
    const row = rows.find(r =>
      r.step_id === step.id ||
      r.step_id === step.key ||
      r._legacy_key === step.key
    );
    if (row) return row.status || 'Not Started';
  }

  return progressData[step.key || step.id] || 'Not Started';
}

export function getOnboardingDisplayState(templateSteps = [], progressData = null, job = null) {
  const useTemplate = templateSteps.length > 0 && progressData?._source !== 'checklist';
  const steps = useTemplate
    ? normalizeTemplateSteps(templateSteps, job)
    : getApplicableSteps(OLD_STEPS, job).map((step, index) => ({
        ...step,
        id: OLD_TO_NEW_STEP_MAP[step.key] || step.key,
        isRequired: true,
        isEnabled: true,
        sortOrder: (index + 1) * 10,
      }));

  const completed = steps.filter(step => getOnboardingStepStatus(progressData, step) === 'Completed').length;
  const requiredSteps = steps.filter(step => step.isRequired !== false);
  const requiredCompleted = requiredSteps.filter(step => getOnboardingStepStatus(progressData, step) === 'Completed').length;

  return {
    steps,
    completed,
    total: steps.length,
    requiredCount: requiredSteps.length,
    requiredCompleted,
    allDone: completed === steps.length,
    allRequiredDone: requiredCompleted === requiredSteps.length,
  };
}

// ─── Promotion Check ───────────────────────────────────────

/**
 * Check if all required steps are complete and promote assignment if so.
 *
 * @param {string} assignmentId
 * @param {Array} templateSteps - from useOnboardingSteps().steps
 * @param {Array} progressRows - current progress data
 * @param {boolean} payConfirmed - whether pay has been confirmed
 * @returns {Object} { shouldPromote, allDone, payConfirmed }
 */
export function checkShouldPromote(templateSteps, progressRows, payConfirmed = false, job = null) {
  const { allRequiredDone, requiredCompleted, requiredCount } = getOnboardingDisplayState(templateSteps, progressRows, job);

  return {
    allDone: allRequiredDone,
    payConfirmed,
    shouldPromote: allRequiredDone && payConfirmed,
    completedCount: requiredCompleted,
    requiredCount,
  };
}

/**
 * Promote an assignment from Onboarding to Active.
 */
export async function promoteAssignment(assignmentId) {
  const { error } = await supabase
    .from('assignment')
    .update({
      status: 'Active',
      started_at: new Date().toISOString(),
    })
    .eq('id', assignmentId);

  if (error) {
    console.error('Failed to promote assignment:', error);
    return { success: false, error };
  }

  // Phase 11 slot 5: assignment lifecycle pivot — Onboarding → Active.
  logAudit(AUDIT_ACTIONS.PROMOTE_ASSIGNMENT, {
    entityType: 'assignment',
    entityId: assignmentId,
    details: { newStatus: 'Active' },
  });

  return { success: true };
}

export default {
  fetchProgress,
  fetchProgressBatch,
  updateStepStatus,
  createProgressForAssignment,
  bypassAllSteps,
  getOnboardingDisplayState,
  getOnboardingStepStatus,
  checkShouldPromote,
  promoteAssignment,
};
